/**
 * Utilitaire de monitoring des Core Web Vitals
 * Mesure et rapporte LCP, FID, CLS, FCP, TTFB
 */

interface WebVitalsMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

type ReportCallback = (metric: WebVitalsMetric) => void;

// Seuils des Core Web Vitals (en ms ou score)
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
  INP: { good: 200, poor: 500 },
};

function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name as keyof typeof THRESHOLDS];
  if (!threshold) return 'good';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

// Mesurer le Largest Contentful Paint (LCP)
export function measureLCP(callback: ReportCallback) {
  if (!('PerformanceObserver' in window)) return;

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & { renderTime?: number; loadTime?: number };
      
      const value = lastEntry.renderTime || lastEntry.loadTime || 0;
      
      callback({
        name: 'LCP',
        value,
        rating: getRating('LCP', value),
        delta: value,
        id: `lcp-${Date.now()}`,
      });
    });

    observer.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch (e) {
    // Silently fail in production
  }
}

// Mesurer le First Input Delay (FID)
export function measureFID(callback: ReportCallback) {
  if (!('PerformanceObserver' in window)) return;

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: PerformanceEntry & { processingStart?: number }) => {
        const value = entry.processingStart ? entry.processingStart - entry.startTime : 0;
        
        callback({
          name: 'FID',
          value,
          rating: getRating('FID', value),
          delta: value,
          id: `fid-${Date.now()}`,
        });
      });
    });

    observer.observe({ type: 'first-input', buffered: true });
  } catch (e) {
    // Silently fail in production
  }
}

// Mesurer le Cumulative Layout Shift (CLS)
export function measureCLS(callback: ReportCallback) {
  if (!('PerformanceObserver' in window)) return;

  let clsValue = 0;
  let sessionValue = 0;
  let sessionEntries: PerformanceEntry[] = [];

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      
      entries.forEach((entry: PerformanceEntry & { value?: number; hadRecentInput?: boolean }) => {
        if (!entry.hadRecentInput) {
          const firstSessionEntry = sessionEntries[0];
          const lastSessionEntry = sessionEntries[sessionEntries.length - 1];

          if (
            sessionValue &&
            entry.startTime - lastSessionEntry.startTime < 1000 &&
            entry.startTime - firstSessionEntry.startTime < 5000
          ) {
            sessionValue += entry.value || 0;
            sessionEntries.push(entry);
          } else {
            sessionValue = entry.value || 0;
            sessionEntries = [entry];
          }

          if (sessionValue > clsValue) {
            clsValue = sessionValue;
            
            callback({
              name: 'CLS',
              value: clsValue,
              rating: getRating('CLS', clsValue),
              delta: clsValue,
              id: `cls-${Date.now()}`,
            });
          }
        }
      });
    });

    observer.observe({ type: 'layout-shift', buffered: true });
  } catch (e) {
    // Silently fail in production
  }
}

// Mesurer le First Contentful Paint (FCP)
export function measureFCP(callback: ReportCallback) {
  if (!('PerformanceObserver' in window)) return;

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.name === 'first-contentful-paint') {
          const value = entry.startTime;
          
          callback({
            name: 'FCP',
            value,
            rating: getRating('FCP', value),
            delta: value,
            id: `fcp-${Date.now()}`,
          });
        }
      });
    });

    observer.observe({ type: 'paint', buffered: true });
  } catch (e) {
    // Silently fail in production
  }
}

// Mesurer le Time to First Byte (TTFB)
export function measureTTFB(callback: ReportCallback) {
  if (!('performance' in window) || !performance.timing) return;

  try {
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    if (navigationEntry) {
      const value = navigationEntry.responseStart - navigationEntry.requestStart;
      
      callback({
        name: 'TTFB',
        value,
        rating: getRating('TTFB', value),
        delta: value,
        id: `ttfb-${Date.now()}`,
      });
    }
  } catch (e) {
    // Silently fail in production
  }
}

// Mesurer l'Interaction to Next Paint (INP) - remplace FID
export function measureINP(callback: ReportCallback) {
  if (!('PerformanceObserver' in window)) return;

  let maxDuration = 0;

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      
      entries.forEach((entry: PerformanceEntry & { duration?: number; processingStart?: number; processingEnd?: number }) => {
        const duration = entry.duration || 0;
        
        if (duration > maxDuration) {
          maxDuration = duration;
          
          callback({
            name: 'INP',
            value: maxDuration,
            rating: getRating('INP', maxDuration),
            delta: maxDuration,
            id: `inp-${Date.now()}`,
          });
        }
      });
    });

    observer.observe({ type: 'event', buffered: true, durationThreshold: 16 });
  } catch (e) {
    // Fallback vers FID si INP n'est pas supporté
    measureFID(callback);
  }
}

// Initialiser tous les Web Vitals
export function initWebVitals(callback: ReportCallback) {
  measureLCP(callback);
  measureFID(callback);
  measureCLS(callback);
  measureFCP(callback);
  measureTTFB(callback);
  measureINP(callback);
}

// Reporter les métriques (console en dev, analytics en prod)
export function reportWebVitals() {
  initWebVitals((metric) => {
    // En développement : afficher dans la console
    if (import.meta.env.DEV) {
      const emoji = metric.rating === 'good' ? '✅' : metric.rating === 'needs-improvement' ? '⚠️' : '❌';
      // eslint-disable-next-line no-console
      console.log(`${emoji} ${metric.name}: ${Math.round(metric.value)}ms (${metric.rating})`);
    }

    // En production : envoyer à Google Analytics ou autre
    if (import.meta.env.PROD && window.gtag) {
      window.gtag('event', metric.name, {
        event_category: 'Web Vitals',
        event_label: metric.id,
        value: Math.round(metric.value),
        metric_rating: metric.rating,
        non_interaction: true,
      });
    }
  });
}

// Déclarer gtag pour TypeScript
declare global {
  interface Window {
    gtag?: (command: string, eventName: string, params: Record<string, unknown>) => void;
  }
}